import clsx from "clsx";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useModels } from "@/hooks/useModels";
import { usePreferences, useUpdatePreferences } from "@/hooks/usePreferences";
import { useStore } from "@/lib/store";
import type {
  FontFacePreference,
  UserPreferences,
  UpdateUserPreferencesInput,
  WebAnswerReadyNotificationsPreference,
} from "@/lib/types";

import { SidebarToggleButton } from "./SidebarToggleButton";
import { getModelLabel } from "./stats/formatters";

type PreferencesDraft = {
  defaultModelId: string | null;
  defaultPersona: string;
  fontFace: FontFacePreference;
  webAnswerReadyNotifications: WebAnswerReadyNotificationsPreference;
};

const EMPTY_DRAFT: PreferencesDraft = {
  defaultModelId: null,
  defaultPersona: "",
  fontFace: "monospace",
  webAnswerReadyNotifications: "not_asked",
};

const FONT_FACE_STORAGE_KEY = "sherlog-font-face";

function readStoredFontFace(): FontFacePreference | null {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(FONT_FACE_STORAGE_KEY);
  return value === "monospace" || value === "inter" ? value : null;
}

function toDraft(preferences: UserPreferences): PreferencesDraft {
  return {
    defaultModelId: preferences.defaultModelId,
    defaultPersona: preferences.defaultPersona ?? "",
    fontFace: readStoredFontFace() ?? preferences.fontFace,
    webAnswerReadyNotifications: preferences.webAnswerReadyNotifications,
  };
}

function areDraftsEqual(a: PreferencesDraft, b: PreferencesDraft): boolean {
  return (
    a.defaultModelId === b.defaultModelId &&
    a.defaultPersona === b.defaultPersona &&
    a.fontFace === b.fontFace &&
    a.webAnswerReadyNotifications === b.webAnswerReadyNotifications
  );
}

async function maybeRequestNotificationPermission(
  preference: WebAnswerReadyNotificationsPreference,
): Promise<"granted" | "denied" | "default" | "unsupported"> {
  if (preference !== "enabled") {
    return "granted";
  }

  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return "unsupported";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  return Notification.requestPermission();
}

export function PreferencesView(): React.JSX.Element {
  const showToast = useStore((state) => state.showToast);
  const setComposerModelId = useStore((state) => state.setComposerModelId);
  const { data: preferencesData, isLoading: isLoadingPreferences } = usePreferences();
  const { data: modelsData, isLoading: isLoadingModels } = useModels();
  const updatePreferences = useUpdatePreferences();

  const [draft, setDraft] = useState<PreferencesDraft>(EMPTY_DRAFT);
  const [savedDraft, setSavedDraft] = useState<PreferencesDraft>(EMPTY_DRAFT);
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!preferencesData?.preferences || hasHydratedRef.current) {
      return;
    }

    const hydrated = toDraft(preferencesData.preferences);
    setDraft(hydrated);
    setSavedDraft(hydrated);
    hasHydratedRef.current = true;
  }, [preferencesData?.preferences]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.documentElement.dataset.fontFace = draft.fontFace;
  }, [draft.fontFace]);

  useEffect(() => {
    return () => {
      if (typeof document !== "undefined") {
        document.documentElement.dataset.fontFace = savedDraft.fontFace;
      }
    };
  }, [savedDraft.fontFace]);

  const isDirty = useMemo(() => !areDraftsEqual(draft, savedDraft), [draft, savedDraft]);

  function handleReset(): void {
    if (!preferencesData?.preferences) {
      return;
    }

    const reset = toDraft(preferencesData.preferences);
    setDraft(reset);
    setSavedDraft(reset);
  }

  function handleSave(): void {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FONT_FACE_STORAGE_KEY, draft.fontFace);
    }

    const payload: UpdateUserPreferencesInput = {
      defaultModelId: draft.defaultModelId,
      defaultPersona: draft.defaultPersona.trim() ? draft.defaultPersona.trim() : null,
      fontFace: draft.fontFace,
      webAnswerReadyNotifications: draft.webAnswerReadyNotifications,
    };

    updatePreferences.mutate(payload, {
      onSuccess: async (result) => {
        const next = toDraft(result.preferences);
        setDraft(next);
        setSavedDraft(next);
        setComposerModelId(next.defaultModelId);
        showToast("Preferences saved", "success");

        const permission = await maybeRequestNotificationPermission(
          next.webAnswerReadyNotifications,
        );
        if (permission === "denied") {
          showToast("Browser notifications are blocked. Enable them in browser settings.", "error");
        } else if (permission === "unsupported") {
          showToast("Browser notifications are not supported in this browser.", "error");
        }
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : "Failed to save preferences";
        showToast(message, "error");
      },
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-cyber-border flex-shrink-0 border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <SidebarToggleButton />
            <h2 className="text-cyber-text text-sm font-medium">Preferences</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={!isDirty || updatePreferences.isPending}
              className="border-cyber-border text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text rounded border px-3 py-1.5 text-xs transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || updatePreferences.isPending}
              className="bg-cyber-cyan disabled:bg-cyber-muted/80 disabled:text-cyber-surface rounded px-3 py-1.5 text-xs text-white transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-100"
            >
              {updatePreferences.isPending ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {isLoadingPreferences ? (
            <div className="text-cyber-muted py-8 text-center">
              <span className="animate-spin-glow mr-2 inline-block">◐</span>
              Loading preferences...
            </div>
          ) : (
            <>
              <section className="border-cyber-border bg-cyber-bg rounded border p-4">
                <h3 className="text-cyber-text mb-1 text-sm">Font face</h3>
                <p className="text-cyber-muted mb-3 text-xs">
                  Changes the default app font across the interface.
                </p>
                <div className="flex gap-2">
                  {(
                    [
                      { value: "monospace", label: "Monospace" },
                      { value: "inter", label: "Inter" },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        setDraft((current) => ({ ...current, fontFace: option.value }))
                      }
                      className={clsx(
                        "rounded border px-3 py-1.5 text-xs tracking-wide uppercase transition-all",
                        draft.fontFace === option.value
                          ? "border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan"
                          : "border-cyber-border text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="border-cyber-border bg-cyber-bg rounded border p-4">
                <h3 className="text-cyber-text mb-1 text-sm">Default model</h3>
                <p className="text-cyber-muted mb-3 text-xs">
                  Pre-selected for new chats. You can still override in the composer.
                </p>
                <div className="relative">
                  <select
                    value={draft.defaultModelId ?? ""}
                    onChange={(e) =>
                      setDraft((current) => ({
                        ...current,
                        defaultModelId: e.target.value.trim() ? e.target.value : null,
                      }))
                    }
                    className="border-cyber-border bg-cyber-surface text-cyber-text focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
                    disabled={isLoadingModels || !modelsData}
                  >
                    <option value="">OpenCode default</option>
                    {modelsData?.models.map((model) => (
                      <option key={model.fullId} value={model.fullId}>
                        {`${model.providerName} · ${getModelLabel(model.providerId, model.modelId, model.modelName)}`}
                      </option>
                    ))}
                  </select>
                  {isLoadingModels && (
                    <span className="text-cyber-muted pointer-events-none absolute top-2.5 right-10">
                      <RefreshCw size={14} className="animate-spin" />
                    </span>
                  )}
                </div>
              </section>

              <section className="border-cyber-border bg-cyber-bg rounded border p-4">
                <h3 className="text-cyber-text mb-1 text-sm">Default persona</h3>
                <p className="text-cyber-muted mb-3 text-xs">
                  Applied only to the first message in a new thread.
                </p>
                <textarea
                  value={draft.defaultPersona}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      defaultPersona: e.target.value.slice(0, 4000),
                    }))
                  }
                  rows={6}
                  placeholder="Example: Be concise, include likely root causes first, and end with next actions."
                  className="border-cyber-border bg-cyber-surface text-cyber-text placeholder-cyber-muted focus:border-cyber-cyan/50 w-full resize-y rounded border px-3 py-2 text-sm focus:outline-none"
                />
                <p className="text-cyber-muted mt-1 text-right text-[11px]">
                  {draft.defaultPersona.length}/4000
                </p>
              </section>

              <section className="border-cyber-border bg-cyber-bg rounded border p-4">
                <h3 className="text-cyber-text mb-1 text-sm">Answer-ready notifications</h3>
                <p className="text-cyber-muted mb-3 text-xs">
                  Controls browser notifications when a response is ready.
                </p>
                <select
                  value={draft.webAnswerReadyNotifications}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      webAnswerReadyNotifications: e.target
                        .value as WebAnswerReadyNotificationsPreference,
                    }))
                  }
                  className="border-cyber-border bg-cyber-surface text-cyber-text focus:border-cyber-cyan/50 w-full rounded border px-3 py-2 text-sm focus:outline-none"
                >
                  <option value="not_asked">Ask me later</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
